<template>
    <div class="ew-page">
        <navbar-dashboard name="Mis Estadísticas"></navbar-dashboard>

        <div class="ew-page__body">
            <h1 class="ew-page__title">Bienvenido a tu resumen de actividad</h1>

            <div class="ew-toolbar">
                <form class="ew-toolbar__search" @submit.prevent="getShipmentFilter">
                    <label class="sr-only" for="ew-shipment-search">Buscar envío</label>
                    <input
                        id="ew-shipment-search"
                        type="text"
                        class="ew-toolbar__input"
                        placeholder="Buscar envío..."
                        v-model.trim="query"
                    >
                    <button type="submit" class="ew-btn ew-btn--outline">Buscar</button>
                </form>
                <router-link class="ew-btn ew-btn--primary" to="/dashboard/shipment/new">Nuevo envío</router-link>
            </div>

            <div class="ew-filters">
                <span class="ew-filters__label"><ph-icon name="funnel" />Seleccione periodo</span>
                <div class="ew-date-presets">
                    <button type="button" class="ew-date-presets__btn" :class="{'is-active': statsPeriod === '30d'}" @click="setStatsPeriod('30d')">Últimos 30 días</button>
                    <button type="button" class="ew-date-presets__btn" :class="{'is-active': statsPeriod === '90d'}" @click="setStatsPeriod('90d')">Últimos 90 días</button>
                    <button type="button" class="ew-date-presets__btn" :class="{'is-active': statsPeriod === 'year'}" @click="setStatsPeriod('year')">Este año</button>
                    <button type="button" class="ew-date-presets__btn" :class="{'is-active': statsPeriod === 'all'}" @click="setStatsPeriod('all')">Todo</button>
                </div>
            </div>

            <div class="ew-grid-2">
                <section class="ew-card">
                    <header class="ew-card__header">
                        <h2 class="ew-card__title">Distribución por transportista</h2>
                    </header>
                    <div class="ew-card__body">
                        <div class="ew-loading" v-if="statsLoading">
                            <span class="ew-spinner"></span>
                            <p class="ew-loading-text">Cargando…</p>
                        </div>
                        <donut-chart v-else :items="stats.byCarrier" />
                    </div>
                </section>

                <section class="ew-card">
                    <header class="ew-card__header">
                        <h2 class="ew-card__title">Tipo de paquete</h2>
                    </header>
                    <div class="ew-card__body">
                        <div class="ew-loading" v-if="statsLoading">
                            <span class="ew-spinner"></span>
                            <p class="ew-loading-text">Cargando…</p>
                        </div>
                        <vertical-bar-chart v-else :items="stats.byPackageType" />
                    </div>
                </section>

                <section class="ew-card">
                    <header class="ew-card__header">
                        <h2 class="ew-card__title">Top 5 ciudades de origen</h2>
                    </header>
                    <div class="ew-card__body">
                        <div class="ew-loading" v-if="statsLoading">
                            <span class="ew-spinner"></span>
                            <p class="ew-loading-text">Cargando…</p>
                        </div>
                        <horizontal-bar-chart v-else :items="stats.topOriginCities" />
                    </div>
                </section>

                <section class="ew-card">
                    <header class="ew-card__header">
                        <h2 class="ew-card__title">Top 5 ciudades de destino</h2>
                    </header>
                    <div class="ew-card__body">
                        <div class="ew-loading" v-if="statsLoading">
                            <span class="ew-spinner"></span>
                            <p class="ew-loading-text">Cargando…</p>
                        </div>
                        <horizontal-bar-chart v-else :items="stats.topDestinationCities" />
                    </div>
                </section>
            </div>

            <section class="ew-card ew-card--shipments">
                <header class="ew-card__header">
                    <h2 class="ew-card__title">Últimos envíos</h2>
                </header>
                <div class="ew-card__body">
                    <div class="ew-loading" v-if="dataLoading">
                        <span class="ew-spinner"></span>
                        <p class="ew-loading-text">Cargando envíos…</p>
                    </div>
                    <div class="table-responsive" v-else-if="lastShipments.length > 0">
                        <table class="ew-table">
                            <thead>
                            <tr>
                                <th scope="col">Destino</th>
                                <th scope="col">Servicio</th>
                                <th scope="col">N° de guía</th>
                                <th scope="col">Total</th>
                                <th scope="col">Guía</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(s, i) in lastShipments" v-bind:key="i">
                                <td>
                                    <span>{{i + 1}}</span> - <span>{{s.destination_direction.full_name }}</span>
                                </td>
                                <td>
                                    <span>{{localCapitalize(s.selected_service.carrier_service_name)}}</span>
                                </td>
                                <td>
                                    {{s.number_guide |numberGuide}}
                                </td>
                                <td class="ew-table__amount" :class="{'is-pending': !s.status, 'is-paid': s.status}">
                                    <small>{{ s.selected_service.currency }}</small> {{ s.selected_service.total_amount }}
                                </td>
                                <td>
                                    <a @click.stop :href="downloadTracking(s)" download :disabled="!s.status" target="_blank" class="ew-table__pdf-link">
                                        <img src="@/assets/images/pdf-icon.svg" v-if="s.status" class="ew-icon-pdf" alt="Descargar guía">
                                        <img src="@/assets/images/pdf-icon-grey.svg" v-else class="ew-icon-pdf" alt="Guía aún no disponible">
                                    </a>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="ew-empty" v-else>
                        <ph-icon name="package" class="ew-empty__icon" />
                        <p class="ew-empty__title">Aún no tienes envíos</p>
                        <p class="ew-empty__text">Cuando generes tu primer envío, aparecerá aquí.</p>
                        <router-link class="ew-btn ew-btn--primary" to="/dashboard/shipment/new">Crear mi primer envío</router-link>
                    </div>
                </div>
            </section>

            <div class="ew-grid-2">
                <section class="ew-card">
                    <header class="ew-card__header">
                        <h2 class="ew-card__title">Estatus de los envíos</h2>
                    </header>
                    <div class="ew-card__body">
                        <table class="ew-table">
                            <thead>
                            <tr>
                                <th scope="col">Estatus</th>
                                <th scope="col" class="text-center">Guías</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>
                                <span>Guías sin pago</span>
                                </td>
                                <td class="text-center">
                                    <span v-if="statusPaymentGuide !== null">
                                        {{statusPaymentGuide.unpaid_guide}}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                <span>Guías pagadas</span>
                                </td>
                                <td class="text-center">
                                     <span v-if="statusPaymentGuide !== null">
                                        {{statusPaymentGuide.paid_guide}}
                                    </span>
                                </td>
                            </tr>
                            <tr class="ew-table__total-row">
                                <td>
                                    <span>Total</span>
                                </td>
                                <td class="text-center" v-if="statusPaymentGuide !== null">
                                    <span>
                                        {{ statusPaymentGuide.unpaid_guide + statusPaymentGuide.paid_guide}}
                                    </span>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section class="ew-card">
                    <header class="ew-card__header">
                        <h2 class="ew-card__title">Envíos por paquetería</h2>
                    </header>
                    <div class="ew-card__body">
                        <table class="ew-table">
                            <thead>
                            <tr>
                                <th scope="col">Paquetería</th>
                                <th scope="col" class="text-center">Guías</th>
                                <th scope="col">Costo</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>
                                <span>DHL</span>
                                </td>
                                <td class="text-center">
                                    <span v-if="parcels !== null">
                                       {{parcels.dhl.guides}}
                                   </span>
                                </td>
                                <td>
                                    <span v-if="parcels !== null">
                                       $ {{parcels.dhl.cost}}
                                   </span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                <span>Fedex</span>
                                </td>
                                <td class="text-center" >
                                   <span v-if="parcels !== null">
                                       {{parcels.fedex.guides}}
                                   </span>
                                </td>
                                <td>
                                   <span v-if="parcels !== null">
                                       $ {{parcels.fedex.cost}}
                                   </span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                <span>Redpack</span>
                                </td>
                                <td class="text-center">
                                    <span v-if="parcels !== null">
                                       {{parcels.redpack.guides}}
                                   </span>
                                </td>
                                <td>
                                    <span v-if="parcels !== null">
                                       $ {{parcels.redpack.cost}}
                                   </span>
                                </td>
                            </tr>
                            <tr class="ew-table__total-row">
                                <td>
                                <span>Total</span>
                                </td>
                                <td class="text-center">
                                    <span v-if="parcels !== null">
                                        {{parcels.dhl.guides + parcels.fedex.guides + parcels.redpack.guides}}
                                   </span>
                                </td>
                                <td>
                                    <span v-if="parcels !== null">
                                       $  {{totalConst(parcels.dhl.cost, parcels.fedex.cost, parcels.redpack.cost)}}
                                   </span>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>
<script>
import Navbar from './Header/Navbar'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import api from '../commons/api'
import axios from 'axios'
import commons from '../commons'
import moment from 'moment'
import PhIcon from './icons/PhIcon'
import HorizontalBarChart from './charts/HorizontalBarChart'
import VerticalBarChart from './charts/VerticalBarChart'
import DonutChart from './charts/DonutChart'


export default {
  name: 'Dashboard',
  data () {
    return {
        query:'',
        dataLoading: true,
        statsPeriod: '30d',
        statsLoading: true,
        stats: {
            byCarrier: [],
            topOriginCities: [],
            topDestinationCities: [],
            byPackageType: []
        }
    }
  },
  created () {
   this.getLastShipments().finally(() => { this.dataLoading = false })
   this.getStatusPaymentGuide()
   this.getParcels()
   this.getUserStats()

  },
  watch:{
      query:function(newValue, oldValue){
          if (newValue === '') {
            this.getLastShipments()
          }
      }
  },
  methods: {
    localCapitalize(string){
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    },
    getShipmentFilter(){
        if (localStorage.getItem('ew-user-id')) {
            let user_id = localStorage.getItem('ew-user-id')
            api.shipment.filterShipment(user_id, this.query).then((response)=>{
                if (response.data.data.length > 0) {
                    this.setLastShipment(response.data.data)
                } else {

                    this.setLastShipment([])
                }
            }).catch((error)=>{
                this.setLastShipment([])
            })
        } else {
            this.setLastShipment([])
        }
    },
    totalConst(dhl, fedex, redpack){
        let total = parseFloat(dhl)  + parseFloat(fedex) + parseFloat(redpack)
        return total.toFixed(3)
    },
    downloadTracking (shipment) {
      if (shipment && shipment.guide && /^https?:\/\//i.test(shipment.guide)) {
        return shipment.guide
      }
      const id = shipment && shipment._id ? shipment._id : shipment
      return `https://www.enviosweb.com.mx/api/public/${id}.pdf`
    },
    setStatsPeriod (period) {
        this.statsPeriod = period
        this.getUserStats()
    },
    getUserStats () {
        const userId = localStorage.getItem('ew-user-id')
        if (!userId) return

        this.statsLoading = true

        let date_ini = null
        let date_fin = null
        const now = moment()
        if (this.statsPeriod === '30d') {
            date_ini = now.clone().subtract(30, 'days').format('YYYY-MM-DD')
            date_fin = now.format('YYYY-MM-DD')
        } else if (this.statsPeriod === '90d') {
            date_ini = now.clone().subtract(90, 'days').format('YYYY-MM-DD')
            date_fin = now.format('YYYY-MM-DD')
        } else if (this.statsPeriod === 'year') {
            date_ini = now.clone().startOf('year').format('YYYY-MM-DD')
            date_fin = now.format('YYYY-MM-DD')
        }
        // 'all' se queda sin date_ini/date_fin: el backend omite el filtro de fecha.

        api.shipment.getUserStats(userId, date_ini, date_fin).then((response) => {
            this.stats = response.data
        }).catch(() => {
            this.stats = { byCarrier: [], topOriginCities: [], topDestinationCities: [], byPackageType: [] }
        }).finally(() => {
            this.statsLoading = false
        })
    },
    ...mapActions(['getLastShipments', 'getStatusPaymentGuide', 'getParcels']),
    ...mapMutations(['setLastShipment', 'setStatusPaymentGuide', 'setParcels'])
  },
  filters:{
    numberGuide:function(number){
        if (number !== null) {
            return number
        } else {
            return 'Sin numero de guía'
        }
    },
    capitalize: function (value) {
        if (!value) return ''
        value = value.toString()
        return value.charAt(0).toUpperCase() + value.slice(1)
    }
  },
  computed: {
      ...mapGetters(['lastShipments', 'parcels', 'statusPaymentGuide']),
  },
  components: {
    'navbar-dashboard': Navbar,
    PhIcon,
    'horizontal-bar-chart': HorizontalBarChart,
    'vertical-bar-chart': VerticalBarChart,
    'donut-chart': DonutChart
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../assets/styles/app-tokens";
@import "../assets/styles/ew-components";

.ew-page {
  @include app-tokens;
}

.ew-loading { @include ew-loading; }
.ew-loading-text { @include ew-loading-text; }
.ew-spinner { @include ew-spinner; }

.ew-page__body {
  padding: 1.75rem 2rem 2.5rem;
}

.ew-page__title {
  margin: 0 0 1.25rem;
  font-family: var(--app-font);
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--app-navy);
}

/* Barra de búsqueda + acción principal --------------------------------- */
.ew-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.ew-toolbar__search {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1 1 auto;
  max-width: 26rem;
}

.ew-toolbar__input {
  flex: 1 1 auto;
  height: 2.75rem;
  padding: 0 0.9rem;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-sm);
  font-family: var(--app-font);
  font-size: 0.9rem;
  color: var(--app-navy);

  &::placeholder { color: var(--app-muted); }
  &:focus {
    outline: none;
    border-color: var(--app-accent);
  }
  &:focus-visible {
    outline: 3px solid var(--app-accent);
    outline-offset: 2px;
  }
}

.ew-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.75rem;
  padding: 0 1.5rem;
  border-radius: 999px;
  font-family: var(--app-font);
  font-size: 0.9rem;
  font-weight: 700;
  border: 1.5px solid transparent;
  white-space: nowrap;

  &:focus-visible {
    outline: 3px solid var(--app-accent);
    outline-offset: 2px;
  }
}

.ew-btn--primary {
  // Relleno de botón con texto: usa --app-accent-button (naranja algo más
  // profundo, 4.91:1 con blanco), no --app-accent (3.80:1, pensado para
  // foco/iconos, no para texto). Ver nota en _app-tokens.scss.
  background: var(--app-accent-button);
  color: var(--app-on-accent);
  &:hover { background: var(--app-accent-button-hover); color: var(--app-on-accent); text-decoration: none; }
}

.ew-btn--outline {
  background: #fff;
  border-color: var(--app-navy);
  color: var(--app-navy);
  &:hover { background: var(--app-surface-muted); }
}

/* Tarjetas --------------------------------------------------------------- */
.ew-card {
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  box-shadow: 0 1px 2px rgba(7, 50, 88, .04);
  margin-bottom: 1.5rem;
}

.ew-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--app-border);
}

.ew-card__title {
  margin: 0;
  font-family: var(--app-font);
  font-size: 1rem;
  font-weight: 700;
  color: var(--app-navy);
}

.ew-card__body {
  padding: 0.25rem 1.25rem 1rem;
}

.ew-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

@media (max-width: 900px) {
  .ew-grid-2 { grid-template-columns: 1fr; }
}

/* Filtro de periodo (gráficas) -------------------------------------------- */
.ew-filters {
  margin-bottom: 1rem;
}

.ew-filters__label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.6rem;
  font-family: var(--app-font);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--app-muted);

  svg { width: 0.9rem; height: 0.9rem; }
}

.ew-date-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.ew-date-presets__btn {
  border: 1px solid var(--app-border);
  background: #fff;
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-family: var(--app-font);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-muted);
  cursor: pointer;

  &:hover { background: var(--app-surface-muted); }
  &.is-active { background: var(--app-navy); border-color: var(--app-navy); color: #fff; }
  &:focus-visible {
    outline: 3px solid var(--app-accent);
    outline-offset: 2px;
  }
}

/* Tablas ------------------------------------------------------------------ */
.ew-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    padding: 0.6rem 0.5rem;
    border-bottom: 1px solid var(--app-border);
    font-family: var(--app-font);
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--app-muted);
    text-align: left;
  }

  tbody td {
    padding: 0.7rem 0.5rem;
    border-bottom: 1px solid var(--app-border);
    font-family: var(--app-font);
    font-size: 0.85rem;
    color: var(--app-navy-secondary);
  }

  tbody tr:last-child td { border-bottom: none; }

  .text-center { text-align: center; }
}

.ew-table__total-row td {
  font-weight: 700;
  color: var(--app-navy);
}

.ew-table__amount {
  font-weight: 700;
  &.is-pending { color: var(--app-accent); }
  &.is-paid { color: #1a7f4f; }
}

.ew-table__pdf-link {
  display: inline-flex;
}

.ew-icon-pdf {
  width: 1.6rem;
  height: auto;
}

/* Estado vacío ------------------------------------------------------------ */
.ew-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  text-align: center;
}

.ew-empty__icon {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--app-muted);
  margin-bottom: 0.25rem;
}

.ew-empty__title {
  margin: 0;
  font-family: var(--app-font);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--app-navy);
}

.ew-empty__text {
  margin: 0 0 0.5rem;
  font-family: var(--app-font);
  font-size: 0.85rem;
  color: var(--app-muted);
}
</style>



// WEBPACK FOOTER //
// src/components/Etatistics.vue